<template>
  <header class="page-header">
    <div class="header-base">
      <div class="header-base__logo-wrapper">
        <div class="header-base__logo">
          <a :href="`${getLinkPrefix}home`">
            <logo-image alt="logo" class="header-base__img" />
          </a>
        </div>

        <div v-if="isDealerMicroSite" class="header-base__dealer-name">
          {{ dealerName }}
        </div>
      </div>
      <button
        :aria-label="burgerLabel"
        class="header-base__burger"
        @click="handleBurgerClick"
      >
        <burger-closed-image
          v-if="!isExpanded"
          alt="menu expander"
          class="header-base__img"
        />
        <burger-opened-image
          v-else
          alt="menu expander"
          class="header-base__img"
        />
      </button>
      <nav class="desktop-links">
        <template v-for="{ text, link, children } in navigationLinks">
          <a
            v-if="link"
            :key="text"
            :href="generateLink(text, link)"
            class="desktop-links__link"
            :class="{
              active: text === $t('shared.used-cars')
            }"
          >
            {{ text }}
          </a>

          <navigation-dropdown
            v-if="children"
            :key="text"
            :items="children"
            :title="text"
          />
        </template>
      </nav>

      <nav class="desktop-links-with-icons">
        <a
          v-for="{ text, link, image } in navigationLinksWithIcons"
          :key="text"
          :href="generateLink(text, link)"
          class="desktop-links-with-icons__link"
          :class="{
            active: text === $t('shared.used-cars')
          }"
        >
          <img
            :src="image"
            :alt="text"
            class="desktop-links-with-icons__image"
          />
          {{ text }}
        </a>
      </nav>
    </div>
    <div v-if="isExpanded" class="header-expand">
      <nav class="links">
        <div
          v-for="{ text, link, children } in navigationLinks"
          :key="text"
          class="links__link-wrapper"
        >
          <a
            v-if="link"
            :href="generateLink(text, link)"
            :class="[
              'links__link',
              text === $t('shared.used-cars') ? 'active' : ''
            ]"
          >
            {{ text }}
          </a>

          <navigation-dropdown-mobile
            v-if="children"
            :key="text"
            :items="children"
            :title="text"
          />
        </div>
        <div class="links__link-with-icon-wrapper">
          <div
            v-for="{
              text,
              description,
              imageMobile,
              link,
              buttonText
            } in navigationLinksWithIcons"
            :key="text"
            class="links__link-with-icon"
          >
            <div class="links__link-with-icon-texts">
              <span class="links__link-with-icon-title">{{ text }}</span>
              <span class="links__link-with-icon-description">{{
                description
              }}</span>
            </div>

            <a :href="link" class="links__link-with-icon-button">
              <img
                :src="imageMobile"
                :alt="text"
                class="desktop-links-with-icons__image"
              />

              <span>{{ buttonText }}</span>
            </a>
          </div>
        </div>
      </nav>
    </div>
  </header>
</template>

<script>
import { mapGetters } from "vuex";

import LogoImage from "@/assets/images/logo.svg";
import BurgerClosedImage from "@/assets/images/burger.svg";
import BurgerOpenedImage from "@/assets/images/close.svg";
import {
  siteLink,
  navigationLinks,
  internalLinks,
  navigationLinksWithIcons,
  navigationLinksWithIconsDealerMicroSite
} from "@/constants/links";
import NavigationDropdown from "@/components/layout/TheHeader/components/NavigationDropdown/NavigationDropdown.vue";
import NavigationDropdownMobile from "@/components/layout/TheHeader/components/NavigationDropdownMobile/NavigationDropdownMobile.vue";

export default {
  name: "TheHeader",
  components: {
    NavigationDropdownMobile,
    NavigationDropdown,
    LogoImage,
    BurgerClosedImage,
    BurgerOpenedImage
  },
  data() {
    return {
      isExpanded: false
    };
  },
  computed: {
    ...mapGetters(["isDealerMicroSite", "dealerName"]),
    getLinkPrefix() {
      return this.$store.state.search.isMobile
        ? siteLink.mobile
        : siteLink.desktop;
    },
    navigationLinks() {
      return navigationLinks;
    },
    navigationLinksWithIcons() {
      return this.isDealerMicroSite
        ? navigationLinksWithIconsDealerMicroSite
        : navigationLinksWithIcons;
    },
    burgerLabel() {
      return this.isExpanded
        ? this.$t("shared.close-navigation")
        : this.$t("shared.open-navigation");
    }
  },
  methods: {
    handleBurgerClick() {
      this.isExpanded = !this.isExpanded;
      this.isExpanded
        ? document.body.classList.add("body-lock")
        : document.body.classList.remove("body-lock");
    },
    generateLink(name, link) {
      if (this.isDealerMicroSite) {
        return `/dealer/${this.$route.params.dealerName}`;
      }

      if (name === this.$t("shared.used-cars")) {
        return internalLinks.home;
      }

      if (link && link.startsWith("http")) return link;

      return this.getLinkPrefix + link;
    }
  }
};
</script>

<style scoped lang="scss">
.desktop-links,
.desktop-links-with-icons {
  display: none;
}

.page-header {
  @include fixed(top 0 left 0 right 0);

  background-color: $color-white;
  z-index: $z-index-6;

  .header-base {
    max-height: $header-height-mobile;
    padding: 10px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    box-shadow: 0 0 20px 0 rgba(0, 0, 0, .1);
    position: relative;
    z-index: $z-index-2;

    &__logo-wrapper {
      display: flex;
      align-items: center;
      margin-right: 20px;
    }

    &__logo {
      height: 25px;
      padding-left: 10px;
    }

    &__dealer-name {
      padding-left: 10px;
      margin-left: 10px;
      border-left: 2px solid $color-blue-300;
      color: $color-blue-300;
      cursor: default;
    }

    &__burger {
      @include sizing(50px 40px);

      padding: 10px;
    }

    &__img {
      height: 100%;
    }
  }

  .header-expand {
    overflow: auto;
    height: 100vh;
    z-index: $z-index-1;
    background-color: $color-nav-background;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    .links {
      &__link-wrapper {
        border-bottom: 1px solid $color-grey-100;
      }

      &__link {
        font-size: 15px;
        font-weight: bold;
        display: block;
        padding: 15px 30px;
        color: $color-grey-500;

        &:hover,
        &.active {
          opacity: .7;
        }
      }

      &__link-with-icon-title {
        font-size: $font-size-l;
        display: block;
        margin-bottom: 5px;
        letter-spacing: .18px;
        color: $color-grey-600;
      }

      &__link-with-icon-description {
        display: block;
        opacity: .8;
        letter-spacing: .14px;
      }

      &__link-with-icon-button {
        background-color: $color-blue-500;
        display: inline-flex;
        justify-content: space-between;
        align-items: center;
        padding: 10px 12px;
        min-width: 120px;
        margin-left: 5px;
        color: $color-white;
        letter-spacing: .11px;
        transition: all .35s;

        &:hover {
          transform: scale(1.1, 1.1);
          opacity: .7;
        }
      }

      &__link-with-icon-wrapper {
        padding: 20px 28px 80px 28px;
      }

      &__link-with-icon {
        background-color: $color-gray-brown;
        padding: 15px 10px;
        margin-bottom: 12px;
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        color: $color-grey-500;
        text-align: left;
      }
    }
  }
}

@media screen and (min-width: $media-l) {
  .page-header .header-base {
    max-height: $header-height-desktop;
    justify-content: flex-start;
    padding: 15px 20px;
  }

  .header-base__burger,
  .header-expand {
    display: none;
  }

  .desktop-links {
    display: flex;
    justify-content: flex-end;

    &__link {
      color: $color-grey-500;
      margin: 0 17px;
      white-space: nowrap;
      font-size: $font-size-nav;
      font-weight: 400;
      transition: all .35s;

      &.active,
      &:hover {
        color: $color-blue-300;
        opacity: .7;
      }
    }
  }

  .desktop-links-with-icons {
    display: flex;
    margin-left: auto;

    &__link {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
      font-size: $font-size-s;
      white-space: nowrap;
      padding-left: 8px;
      padding-right: 8px;
      transition: all .35s;

      &:hover {
        color: $color-blue-300;
        opacity: .7;
      }
    }

    &__image {
      width: 24px;
      height: 24px;
      object-fit: contain;
      margin-bottom: 5px;

      /* this is a pollyfill for using object-fit on ie11 */
      /* stylelint-disable-next-line */
      font-family: "object-fit: contain;";
    }
  }
}

@media screen and (min-width: $media-xl) {
  .page-header {
    @include grid-columns(12);

    box-shadow: 0 0 20px 0 rgba(0, 0, 0, .1);

    .header-base {
      @include grid-columns-from-to(2, 12);

      box-shadow: none;
    }
  }
}
</style>
